import React, { useState, useEffect, useMemo } from "react";
import Select from "../../Select";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
} from "recharts";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
const NativeAnalysis = ({ content }) => {
  if (!content || typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      }
      if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {content.split("\n").map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1" />;

        if (trimmed.startsWith("### ")) {
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        }

        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">•</span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

const yAxisLabelHa = "1000 ha";
const yAxisDxHa = -yAxisLabelHa.length;

const landUseGroups = [
  ["pasture"],
  ["forest"],
  ["cropland"],
  ["otherLand"],
  ["urban"],
];

const regionalLandCoverOptions = [
  "Pasture",
  "Forest",
  "Cropland",
  "Otherland",
  "Urban",
];

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg
      className="animate-spin h-5 w-5 text-[#59AD31]"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      ></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

function getGroupedOrderByMaxValue(data, groups) {
  const safeData = Array.isArray(data) ? data : [];
  const safeGroups = Array.isArray(groups) ? groups : [];

  if (safeData.length === 0) return safeGroups.flat();

  const sortedGroups = [...safeGroups].sort((a, b) => {
    const keyA = Array.isArray(a) ? a[0] : a;
    const keyB = Array.isArray(b) ? b[0] : b;

    const maxA = Math.max(
      ...safeData.map((d) => (typeof d?.[keyA] === "number" ? d[keyA] : 0)),
    );
    const maxB = Math.max(
      ...safeData.map((d) => (typeof d?.[keyB] === "number" ? d[keyB] : 0)),
    );

    return maxB - maxA;
  });

  return sortedGroups.flat();
}

function getDynamicColor(index) {
  const palette = [
    "#183B8C",
    "#A7A9AC",
    "#10B24A",
    "#72BCE3",
    "#F6B800",
    "#C65A11",
    "#FF5579",
    "#9999CC",
    "#009B8A",
    "#FB752E",
    "#6C5CE7",
    "#00B894",
    "#E17055",
    "#0984E3",
    "#D63031",
    "#636E72",
    "#00CEC9",
    "#E84393",
    "#2D3436",
    "#FDCB6E",
  ];

  return palette[index % palette.length];
}

function CustomTooltip({ active, payload, label, chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 text-[11px] shadow-md">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>

      {sortedPayload.map((item, idx) => {
        const isTarget = item.dataKey === "targetShare";
        let icon;

        if (isTarget) {
          icon = (
            <svg width="16" height="3" viewBox="0 0 16 3" className="inline">
              <line x1="0" y1="1.5" x2="16" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="3,2" />
            </svg>
          );
        } else if (chartType === "line") {
          icon = (
            <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
              <line x1="0" y1="1" x2="16" y2="1" stroke={item.color} strokeWidth="2" />
            </svg>
          );
        } else {
          icon = (
            <span
              className="h-2 w-2 rounded-[2px]"
              style={{ backgroundColor: item.color }}
            />
          );
        }

        return (
          <div
            key={`${item.dataKey}-${idx}`}
            className="mb-0.5 flex items-center justify-between gap-2"
          >
            <div className="flex items-center gap-1.5">
              {icon}
              <span className="text-[#7E8081]">{item.name}</span>
            </div>

            <span className="font-medium text-[#323334]">
              {typeof item.value === "number"
                ? item.value.toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })
                : item.value}{" "}
              <span className="text-[#98999A]">{yAxisLabelHa}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function RegionalAreaTooltip({ active, payload, label, chartType = "area" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 text-[11px] shadow-md">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>

      {sortedPayload.map((item, idx) => {
        const isTarget = item.dataKey === "targetShare";
        let icon;

        if (isTarget) {
          icon = (
            <svg width="16" height="3" viewBox="0 0 16 3" className="inline">
              <line x1="0" y1="1.5" x2="16" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="3,2" />
            </svg>
          );
        } else if (chartType === "area") {
          icon = (
            <span
              className="h-2 w-3 rounded-[1px]"
              style={{ backgroundColor: item.color }}
            />
          );
        } else {
          icon = (
            <span
              className="h-2 w-2 rounded-[2px]"
              style={{ backgroundColor: item.color }}
            />
          );
        }

        return (
          <div
            key={`${item.dataKey}-${idx}`}
            className="mb-0.5 flex items-center justify-between gap-2"
          >
            <div className="flex items-center gap-1.5">
              {icon}
              <span className="text-[#7E8081]">{item.name}</span>
            </div>

            <span className="font-medium text-[#323334]">
              {typeof item.value === "number"
                ? item.value.toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })
                : item.value}{" "}
              <span className="text-[#98999A]">{yAxisLabelHa}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function OrderedLegend({ payload, order, hiddenKeys, onToggle, chartType = "bar" }) {
  if (!payload) return null;

  const ordered = [...payload].sort(
    (a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey),
  );

  return (
    <div className="mb-2 flex w-full flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {ordered.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);

        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
          >
            {chartType === "line" ? (
              <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
                <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color || "#9999CC"} strokeWidth="2" />
              </svg>
            ) : (
              <span
                className="h-[3px] w-4 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}
            <span className="text-[12px]">{entry.value}</span>
          </button>
        );
      })}
    </div>
  );
}

function RegionalLegend({ payload, hiddenKeys, onToggle, chartType = "area" }) {
  if (!payload) return null;

  return (
    <div className="mb-2 flex w-full flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {payload.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);

        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
          >
            {chartType === "area" ? (
              <span
                className="h-3 w-4 rounded-[1px]"
                style={{ backgroundColor: entry.color }}
              />
            ) : (
              <span
                className="h-[3px] w-4 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}
            <span className="text-[12px]">{entry.value}</span>
          </button>
        );
      })}
    </div>
  );
}

function getCountryLandSeries(block, selectedCountry) {
  if (Array.isArray(block)) return block;
  if (!block || typeof block !== "object") return [];

  // Si se selecciona "Global", devolver directamente los datos de Global del servidor
  if (selectedCountry === "Global") {
    const globalSeries = block["Global"];
    return Array.isArray(globalSeries) ? globalSeries : [];
  }

  const maybeSeries = block[selectedCountry];
  return Array.isArray(maybeSeries) ? maybeSeries : [];
}

function resolvePathwayNode(inputData, pathway) {
  if (!inputData || typeof inputData !== "object") return null;

  if (Array.isArray(inputData.charts)) return inputData;

  if (pathway && inputData[pathway] && typeof inputData[pathway] === "object") {
    return inputData[pathway];
  }

  const keys = Object.keys(inputData);
  if (keys.length === 1 && typeof inputData[keys[0]] === "object") {
    const candidate = inputData[keys[0]];
    if (Array.isArray(candidate?.charts)) return candidate;
  }

  return null;
}

function resolveLandUseBlock(inputData, pathway) {
  if (!inputData || typeof inputData !== "object") return null;

  if (Array.isArray(inputData?.countries) || inputData?.["Area by landcover"]) {
    return inputData;
  }

  if (inputData?.["Land use"]) {
    return inputData["Land use"];
  }

  const pathwayNode = resolvePathwayNode(inputData, pathway);
  if (pathwayNode?.charts?.[0]?.["Land use"]) {
    return pathwayNode.charts[0]["Land use"];
  }

  return null;
}

function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }
  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }
  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }
  return "Could not load analysis at this time.";
}

export default function LandUseTab({ data, pathway, pathwayId, scenathonId }) {
  const landUseBlock = useMemo(
    () => resolveLandUseBlock(data, pathway),
    [data, pathway],
  );

  const resolvedPathwayNode = useMemo(
    () => resolvePathwayNode(data, pathway),
    [data, pathway],
  );

  const effectivePathwayId = pathwayId || resolvedPathwayNode?.id || null;

  const countries = useMemo(
    () => (Array.isArray(landUseBlock?.countries) ? landUseBlock.countries : []),
    [landUseBlock],
  );

  const defaultCountry = useMemo(() => {
    if (countries.includes("Global")) return "Global";
    return countries[0] || "";
  }, [countries]);

  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [selectedRegionalCover, setSelectedRegionalCover] = useState("Pasture");
  const [hiddenLandKeys, setHiddenLandKeys] = useState([]);
  const [hiddenRegionalKeys, setHiddenRegionalKeys] = useState([]);
  const [landAnalysis, setLandAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [regionalAnalysis, setRegionalAnalysis] = useState("");
  const [loadingRegionalAnalysis, setLoadingRegionalAnalysis] = useState(false);

  useEffect(() => {
    if (!selectedCountry && defaultCountry) {
      setSelectedCountry(defaultCountry);
    } else if (
      selectedCountry &&
      countries.length > 0 &&
      !countries.includes(selectedCountry)
    ) {
      setSelectedCountry(defaultCountry);
    }
  }, [selectedCountry, defaultCountry, countries]);

  const chartData = useMemo(() => {
    const block = landUseBlock?.["Area by landcover"];
    return getCountryLandSeries(block, selectedCountry);
  }, [landUseBlock, selectedCountry]);

  const regionalLandUseBlock = useMemo(() => {
    const block = landUseBlock?.["Area by landcover by region"];
    return block && typeof block === "object" ? block : null;
  }, [landUseBlock]);

  const regionalSeriesLabels = useMemo(() => {
    return Array.isArray(regionalLandUseBlock?.labels)
      ? regionalLandUseBlock.labels
      : [];
  }, [regionalLandUseBlock]);

  const regionalChartData = useMemo(() => {
    const metricBlock = regionalLandUseBlock?.seriesByMetric;
    if (!metricBlock || typeof metricBlock !== "object") return [];

    const series = metricBlock[selectedRegionalCover];
    return Array.isArray(series) ? series : [];
  }, [regionalLandUseBlock, selectedRegionalCover]);

  const countryOptions = useMemo(
    () =>
      countries.map((countryName) => ({
        value: countryName,
        label: countryName,
      })),
    [countries],
  );

  const regionalCoverOptions = useMemo(
    () =>
      regionalLandCoverOptions.map((item) => ({
        value: item,
        label: item,
      })),
    [],
  );

  const landLegendOrder = useMemo(() => {
    return getGroupedOrderByMaxValue(
      Array.isArray(chartData) ? chartData : [],
      landUseGroups,
    );
  }, [chartData]);

  useEffect(() => {
    if (!scenathonId || !pathway || !selectedCountry || !Array.isArray(chartData) || chartData.length === 0) {
      setLandAnalysis("");
      setLoadingAnalysis(false);
      return;
    }

    const controller = new AbortController();

    const fetchAnalysis = async () => {
      setLoadingAnalysis(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              analysisKey: "land_use_analysis",
              scope: "country",
              scenathonId,
              pathway,
              country: selectedCountry,
              landUseData: {
                data: JSON.stringify(chartData),
                title: `Area by land cover - ${selectedCountry}`,
              },
              context: "land",
            }),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setLandAnalysis(
          extractAnalysisText(resData, ["landUseAnalysis", "analysis"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching analysis:", error);
          setLandAnalysis("Cannot load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingAnalysis(false);
        }
      }
    };

    fetchAnalysis();

    return () => controller.abort();
  }, [chartData, pathway, scenathonId, selectedCountry]);

  useEffect(() => {
    if (
      !scenathonId ||
      !pathway ||
      !selectedRegionalCover ||
      !Array.isArray(regionalChartData) ||
      regionalChartData.length === 0
    ) {
      setRegionalAnalysis("");
      setLoadingRegionalAnalysis(false);
      return;
    }

    const controller = new AbortController();

    const fetchRegionalAnalysis = async () => {
      setLoadingRegionalAnalysis(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              analysisKey: "land_use_by_region_analysis",
              scope: "metric",
              metric: selectedRegionalCover,
              scenathonId,
              pathway,
              landUseRegionalData: {
                data: JSON.stringify(regionalChartData),
                title: `Area by land cover by region - ${selectedRegionalCover}`,
              },
              context: "land",
            }),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setRegionalAnalysis(
          extractAnalysisText(resData, ["landUseByRegionAnalysis", "analysis"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching regional analysis:", error);
          setRegionalAnalysis("Cannot load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingRegionalAnalysis(false);
        }
      }
    };

    fetchRegionalAnalysis();

    return () => controller.abort();
  }, [regionalChartData, pathway, scenathonId, selectedRegionalCover]);

  const handleLegendClickLand = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;

    setHiddenLandKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickRegional = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;

    setHiddenRegionalKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  return (
    <div className="flex w-full flex-col gap-10">
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Area by land cover
          </h2>

          <div className="w-full max-w-sm">
            <Select
              label="Country"
              value={selectedCountry}
              onChange={setSelectedCountry}
              options={countryOptions}
              placeholder="Select country"
              disabled={countryOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={Array.isArray(chartData) ? chartData : []}
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>

              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", {
                    maximumFractionDigits: 1,
                  })
                }
              >
                <Label
                  value={yAxisLabelHa}
                  angle={-90}
                  position="left"
                  dx={yAxisDxHa}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>

              <Tooltip
                content={(props) => <CustomTooltip {...props} chartType="line" />}
                cursor={{ stroke: "rgba(0,0,0,0.15)", strokeWidth: 1 }}
              />

              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{ top: 0, left: 0 }}
                content={(props) => (
                  <OrderedLegend
                    {...props}
                    order={landLegendOrder}
                    hiddenKeys={hiddenLandKeys}
                    onToggle={handleLegendClickLand}
                    chartType="line"
                  />
                )}
              />

              <Line
                type="monotone"
                dataKey="pasture"
                name="Pasture"
                stroke="#FB752E"
                strokeWidth={2}
                dot={false}
                hide={hiddenLandKeys.includes("pasture")}
              />

              <Line
                type="monotone"
                dataKey="forest"
                name="Forest"
                stroke="#2FCD76"
                strokeWidth={2}
                dot={false}
                hide={hiddenLandKeys.includes("forest")}
              />

              <Line
                type="monotone"
                dataKey="cropland"
                name="Cropland"
                stroke="#80D5FF"
                strokeWidth={2}
                dot={false}
                hide={hiddenLandKeys.includes("cropland")}
              />
              <Line
                type="monotone"
                dataKey="otherLand"
                name="Otherland"
                stroke="#FF5579"
                strokeWidth={2}
                dot={false}
                hide={hiddenLandKeys.includes("otherLand")}
              />

              <Line
                type="monotone"
                dataKey="urban"
                name="Urban"
                stroke="#9999CC"
                strokeWidth={2}
                dot={false}
                hide={hiddenLandKeys.includes("urban")}
              />

            </LineChart>
          </ResponsiveContainer>
        </div>
        {/* <div className="w-full min-h-[100px]">
          {loadingAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={landAnalysis} />
          )}
        </div> */}
      </section>

      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Area by land cover by region
          </h2>

          <div className="w-full max-w-sm">
            <Select
              label="Select"
              value={selectedRegionalCover}
              onChange={setSelectedRegionalCover}
              options={regionalCoverOptions}
              placeholder="Select land cover"
              disabled={regionalCoverOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={Array.isArray(regionalChartData) ? regionalChartData : []}
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>

              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", {
                    maximumFractionDigits: 1,
                  })
                }
              >
                <Label
                  value={yAxisLabelHa}
                  angle={-90}
                  position="left"
                  dx={yAxisDxHa}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>

              <Tooltip
                content={(props) => <RegionalAreaTooltip {...props} chartType="area" />}
                cursor={{ stroke: "rgba(0,0,0,0.15)", strokeWidth: 1 }}
              />

              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{ top: 0, left: 0 }}
                content={(props) => (
                  <RegionalLegend
                    {...props}
                    hiddenKeys={hiddenRegionalKeys}
                    onToggle={handleLegendClickRegional}
                    chartType="area"
                  />
                )}
              />

              {regionalSeriesLabels.map((label, index) => {
                const color = getDynamicColor(index);

                return (
                  <Area
                    key={label}
                    type="monotone"
                    dataKey={label}
                    stackId="1"
                    name={label}
                    stroke={color}
                    fill={color}
                    hide={hiddenRegionalKeys.includes(label)}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* <div className="w-full min-h-[100px]">
          {loadingRegionalAnalysis ? <LoadingAnalysisHelper /> : <NativeAnalysis content={regionalAnalysis} />}
        </div> */}
      </section>
    </div>
  );
}